import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../config/api";

export default function ResetPassword() {
  const { token } = useParams();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const [checking, setChecking] = useState(true);

  // ✅ VERIFY TOKEN
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/verify_token.php?token=${token}`
        );

        if (res.data.success) {
          setValidToken(true);
        } else {
          setError("This reset link is expired or invalid.");
        }
      } catch {
        setError("Invalid request. Please try again.");
      } finally {
        setChecking(false);
      }
    };

    verifyToken();
  }, [token]);

  const validate = () => {
    if (!password || !confirmPassword) {
      setError("Please fill in all required fields.");
      return false;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return false;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!validate()) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/reset_password.php`,
        { token: token, password: password },
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.data.success) {
        setMsg(res.data.message);
        setPassword("");
        setConfirmPassword("");
        setError("");

        // redirect after 3 sec
        setTimeout(() => {
          window.location.href = "/login";
        }, 3000);
      } else {
        setError(res.data.message || "Failed to reset password.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ⏳ Loading state
  if (checking) {
    return (
      <section className="gray-simple">
        <div className="container text-center mt-5">
          <p>Checking reset link...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="gray-simple">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-5 col-lg-6 col-md-8 pt-2">
            <div className="card rounded-4 shadow-sm">
              <div className="card-body p-4">

                {/* Title */}
                <h3
                  className="fw-bold text-center mb-2"
                  style={{ color: "rgb(23, 111, 176)" }}
                >
                  Reset Your Password
                </h3>

                <p className="text-center text-muted mb-4">
                  Enter your new password below to complete the reset process.
                </p>

                {/* Alerts */}
                {error && (
                  <div className="alert alert-danger text-center py-2">
                    {error}
                  </div>
                )}

                {msg && (
                  <div className="alert alert-success text-center py-2">
                    {msg} <br />
                    Redirecting to login...
                  </div>
                )}

                {/* ❌ Hide form if token invalid */}
                {validToken && (
                  <form onSubmit={handleSubmit}>
                    <div className="form-group mb-4">
                      <label className="fw-medium text-dark">
                        New Password <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        className={`form-control ${
                          error ? "is-invalid" : ""
                        }`}
                        placeholder="Enter new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>

                    <div className="form-group mb-4">
                      <label className="fw-medium text-dark">
                        Confirm Password <span className="text-danger">*</span>
                      </label>
                      <input
                        type="password"
                        className={`form-control ${
                          error ? "is-invalid" : ""
                        }`}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) =>
                          setConfirmPassword(e.target.value)
                        }
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Password"}
                    </button>

                    <p className="text-center mt-4 mb-0">
                      <Link
                        to="/login"
                        className="text-decoration-none fw-medium"
                      >
                        Back to Login
                      </Link>
                    </p>
                  </form>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}